import { lazy, Suspense, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { queryClient } from './services/queryClient.js';
import { bootstrapSession } from './services/auth.js';
import { router } from './router.jsx';

/* Dev-only. The `import.meta.env.DEV` branch is statically false in a production
   build, so the devtools import is eliminated rather than shipped. */
const Devtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
    )
  : null;

/**
 * Provider composition, plus the one thing that has to happen before the route
 * guards can decide anything.
 *
 *   QueryClientProvider  — server state, so any route can call useQuery
 *   RouterProvider       — the route map
 *
 * The session bootstrap is a single POST /auth/refresh that either restores the
 * session from the httpOnly cookie or settles the auth store on 'anonymous'. It
 * lives here rather than in a route loader because it has to run once per page
 * load, not once per navigation, and it is fire-and-forget on purpose: the store
 * starts at 'unknown', `RequireAuth` renders its pending line while the request
 * is in flight, and nothing else waits on it.
 *
 * StrictMode invokes this effect twice in development. That is harmless rather
 * than merely tolerated — `refreshSession()` is single-flight, so the second
 * call joins the first request instead of issuing another one.
 */
export default function App() {
  useEffect(() => {
    bootstrapSession();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {Devtools && (
        <Suspense fallback={null}>
          <Devtools initialIsOpen={false} buttonPosition="bottom-left" />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
